/**
 * The read queries (TanStack Query). Each refetches when the window regains focus, so a screen left open
 * at the desk is current when someone looks back at it; `<Updated/>` shows when it last loaded.
 */
import type { ProgramKey } from '@shared/people';
import { VIEW_CODES } from '@shared/people-programs';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type {
  AppointmentRangeResponse,
  AttentionResponse,
  CyclesResponse,
  DayPlanResponse,
  SlotsResponse,
  TenantAppointmentsResponse,
  TenantDetail,
  TenantSearchResponse,
  TenantStats,
  TodayResponse,
} from '@shared/api-types';
import { apiFetch } from './api';

const live = { refetchOnWindowFocus: true, staleTime: 30_000 } as const;

const todayQuery = (day: string) => ({ queryKey: ['today', day], queryFn: () => apiFetch<TodayResponse>(`/api/today?day=${day}`), ...live });

export function useToday(day: string) {
  return useQuery(todayQuery(day));
}

/**
 * Start Today's read the moment the app opens, beside the sign-in check instead of after it (and after
 * Today's code arrives), so the first screen fills sooner. A signed-out answer is simply dropped.
 */
export function prefetchToday(qc: QueryClient, day: string): void {
  void qc.prefetchQuery({ ...todayQuery(day), retry: false });
}

export function useAttention(enabled = true) {
  return useQuery({ queryKey: ['attention'], queryFn: () => apiFetch<AttentionResponse>('/api/attention'), enabled, ...live });
}

export function useDayPlan(enabled = true) {
  return useQuery({ queryKey: ['dayplan'], queryFn: () => apiFetch<DayPlanResponse>('/api/dayplan'), enabled, staleTime: 5 * 60_000, refetchOnWindowFocus: true });
}

export function useAppointments(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['appointments', from, to],
    queryFn: () => apiFetch<AppointmentRangeResponse>(`/api/appointments?from=${from}&to=${to}`),
    enabled,
    ...live,
  });
}

export function useSlots(from: string, to: string, enabled = true) {
  return useQuery({ queryKey: ['slots', from, to], queryFn: () => apiFetch<SlotsResponse>(`/api/slots?from=${from}&to=${to}`), enabled, ...live });
}

export function useCycles(enabled = true) {
  return useQuery({ queryKey: ['cycles'], queryFn: () => apiFetch<CyclesResponse>('/api/cycles'), enabled, staleTime: 10 * 60_000 });
}

/** With a program (1.5.9) the server puts the program's enrollees first and marks everyone else not enrolled. */
export function useTenantSearch(q: string, program?: ProgramKey) {
  const query = q.trim();
  return useQuery({
    queryKey: ['tenant-search', query.toLowerCase(), program ?? ''],
    queryFn: () => apiFetch<TenantSearchResponse>(`/api/tenants?q=${encodeURIComponent(query)}${program ? `&program=${VIEW_CODES[program]}` : ''}`),
    enabled: query.length > 0,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

const tid = (id: string) => encodeURIComponent(id);

export function useTenant(id: string | null) {
  return useQuery({ queryKey: ['tenant', id], queryFn: () => apiFetch<TenantDetail>(`/api/tenants/${tid(id!)}`), enabled: !!id, ...live });
}

export function useTenantHistory(id: string | null) {
  return useQuery({
    queryKey: ['tenant-history', id],
    queryFn: () => apiFetch<TenantAppointmentsResponse>(`/api/tenants/${tid(id!)}/appointments`),
    enabled: !!id,
    ...live,
  });
}

export function useTenantStats(id: string | null) {
  return useQuery({ queryKey: ['tenant-stats', id], queryFn: () => apiFetch<TenantStats>(`/api/tenants/${tid(id!)}/stats`), enabled: !!id, ...live });
}
